// audiences.ts — (tf3-42, B90; planning 10-21 16:45) Admin → Audiences, pure: the profile form, an axis at a time, as the
// registry stores it — ABSENT inherits the parent's value, NULL is "deliberately none", anything else is the value
// (@vms/tubeforge-shared's AUDIENCE_AXIS_REGISTRY, its value lists, E1). The child band's words. The backend's API checks
// the axes (validateAudienceAxes, 422 with every problem) and reports narrowing problems on its resolve (Preview).
import { AUDIENCE_AXIS_REGISTRY } from "@vms/tubeforge-shared";

export type Mode = "inherit" | "none" | "value";
export interface AxisField { key: string; label: string; kind: "select" | "text" | "lines"; values?: readonly string[] }
/** Each axis of the registry, in its order, then the visual world (its settings and aesthetic). */
export const AXIS_LABELS: Record<string, string> = {
  niche: "Niche", gender: "Gender", ageBand: "Age band", lifeStage: "Life stage", sophistication: "Sophistication", resources: "Resources",
  coreDriver: "Core driver", pain: "Pain points", aspiration: "Aspiration", needsToHear: "Needs to hear", interests: "Interests",
  wantsToSee: "Wants to see", languageTone: "Language and tone", antiPatterns: "Anti-patterns",
};
export const AXIS_FIELDS: readonly AxisField[] = AUDIENCE_AXIS_REGISTRY.map((a) => ({
  key: a.key, label: AXIS_LABELS[a.key] ?? a.key,
  kind: a.values ? "select" : a.merge === "UNION" ? "lines" : "text", ...(a.values ? { values: a.values } : {}),
}));
/** (planning 10-21 12:25, approved 17:35) the child band: a band TubeForge makes nothing for */
export const CHILD_LABEL = "Under 13: no ideas are made for this audience";
export const bandLabel = (v: string) => (v === "child" ? `child — ${CHILD_LABEL}` : v);
export const isChild = (resolvedAgeBand: string | null | undefined) => resolvedAgeBand === "child";

export interface FieldState { mode: Mode; text: string }
export type FormState = Record<string, FieldState> & { visualWorld: FieldState & { aesthetic: string } };
const lines = (t: string) => t.split("\n").map((x) => x.trim()).filter(Boolean);

/** Pure: a profile's own node (as GET /admin/audiences/:id answers it) → the form. */
export function formOf(p: Record<string, unknown> | null): FormState {
  const f: Record<string, FieldState> = {};
  for (const a of AXIS_FIELDS) {
    const has = !!p && Object.prototype.hasOwnProperty.call(p, a.key), v = p?.[a.key];
    f[a.key] = !has ? { mode: "inherit", text: "" } : v === null ? { mode: "none", text: "" } : { mode: "value", text: Array.isArray(v) ? v.join("\n") : String(v) };
  }
  const vw = p?.visualWorld as { settings?: string[]; aesthetic?: string } | null | undefined;
  const hasVw = !!p && Object.prototype.hasOwnProperty.call(p, "visualWorld");
  return { ...f, visualWorld: !hasVw ? { mode: "inherit", text: "", aesthetic: "" } : vw === null ? { mode: "none", text: "", aesthetic: "" }
    : { mode: "value", text: (vw?.settings ?? []).join("\n"), aesthetic: vw?.aesthetic ?? "" } } as FormState;
}

/** Pure: the form → the axes the API takes (inherit: left out; none: null; a value in its type), or what's wrong. */
export function axesOf(f: FormState): { axes: Record<string, unknown> } | { error: string } {
  const axes: Record<string, unknown> = {};
  for (const a of AXIS_FIELDS) {
    const s = f[a.key] ?? { mode: "inherit", text: "" };
    if (s.mode === "inherit") continue;
    if (s.mode === "none") { axes[a.key] = null; continue; }
    if (a.kind === "lines") { const l = lines(s.text); if (!l.length) return { error: `${a.label}: one per line, or choose None` }; axes[a.key] = l; continue; }
    const t = s.text.trim();
    if (!t) return { error: `${a.label}: a value, or choose Inherit or None` };
    if (a.values && !a.values.includes(t)) return { error: `${a.label}: one of ${a.values.join(", ")}` };
    axes[a.key] = t;
  }
  const vw = f.visualWorld;
  if (vw.mode === "none") axes.visualWorld = null;
  else if (vw.mode === "value") {
    const settings = lines(vw.text), aesthetic = vw.aesthetic.trim();
    if (!settings.length && !aesthetic) return { error: "Visual world: settings or an aesthetic, or choose Inherit or None" };
    axes.visualWorld = { ...(settings.length ? { settings } : {}), ...(aesthetic ? { aesthetic } : {}) };
  }
  return { axes };
}

/** The backend's id rule (audience-profiles.repo SLUG_ID_RE), the same as contracts' SlugId a slate needs. */
export const PROFILE_ID = /^[a-z][a-z0-9_]{1,62}$/;
export const DELETE_CHILDREN_FIRST = "Delete its children first.";
export const DELETE_NOTE = "What the slates learned for this audience stays keyed by its id: a new profile with the same id would pick it up.";
const msgOf = (e: unknown) => (e as { message?: string } | null)?.message?.trim() || "not done; try again";
/** Pure: a delete's refusal in words — the API's 409 is a profile with children. */
export const deleteErrorText = (e: unknown) => ((e as { status?: number } | null)?.status === 409 ? `${DELETE_CHILDREN_FIRST} (${msgOf(e)})` : msgOf(e));
export const errorText = msgOf;
